import React from "react";
import { skills } from "../mock";
import * as Icons from "lucide-react";
import { getSkillIcon } from "../lib/skillIcons";

const SkillChip = ({ name }) => {
  const Icon = getSkillIcon(name);
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border border-[#a01d3f]/45 bg-gradient-to-br from-[#a01d3f]/30 via-[#a01d3f]/15 to-[#a01d3f]/8 text-[#f4f1ea] transition-all duration-200 hover:border-[#a01d3f] hover:from-[#a01d3f]/55 hover:to-[#a01d3f]/25 hover:scale-[1.06] hover:shadow-[0_4px_14px_-3px_rgba(160,29,63,0.6)] cursor-default">
      <Icon className="w-3.5 h-3.5 shrink-0 text-[#f4f1ea]" />
      <span>{name}</span>
    </span>
  );
};

const SkillCategoryCard = ({ group }) => {
  const Icon = Icons[group.icon] || Icons.Sparkles;
  return (
    <div className="group relative rounded-2xl p-6 md:p-7 bg-gradient-to-br from-[#a01d3f]/10 via-[#121212] to-[#0f0f0f] border border-[#a01d3f]/30 overflow-hidden transition-all duration-300 hover:border-[#a01d3f] hover:-translate-y-1 hover:shadow-[0_20px_50px_-18px_rgba(160,29,63,0.55)]">
      {/* Decorative bordeaux blur */}
      <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-[#a01d3f]/20 blur-3xl pointer-events-none opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />

      {/* Header row */}
      <div className="relative flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#a01d3f] to-[#861732] flex items-center justify-center shrink-0 shadow-[0_5px_16px_-2px_rgba(160,29,63,0.55)] group-hover:shadow-[0_8px_22px_-2px_rgba(160,29,63,0.75)] transition-shadow duration-300">
            <Icon size={17} strokeWidth={1.7} className="text-[#f4f1ea]" />
          </div>
          <div>
            <h3 className="font-display text-lg md:text-xl font-semibold tracking-tight leading-none">
              {group.category}
            </h3>
            <span className="font-mono text-[10px] text-[#f4f1ea]/45 mt-1.5 inline-block">
              {group.code}
            </span>
          </div>
        </div>
        <span className="font-mono text-[10px] text-[#a01d3f] mt-1">
          [{group.items.length}]
        </span>
      </div>

      {/* Chips */}
      <div className="relative flex flex-wrap gap-2">
        {group.items.map((item) => (
          <SkillChip key={item} name={item} />
        ))}
      </div>

      {/* Bottom shimmer line on hover */}
      <span className="absolute left-6 right-6 bottom-0 h-px bg-gradient-to-r from-transparent via-[#a01d3f] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
  );
};

const Skills = () => {
  return (
    <section
      id="skills"
      className="section-padding bg-[#0a0a0a] text-[#f4f1ea] relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-px h-32 bg-gradient-to-b from-[#a01d3f] to-transparent pointer-events-none" />

      <div className="container-x relative">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-12 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#a01d3f] mb-6">
              <span className="text-[#f4f1ea]/40">[ 02 ]</span> &nbsp;—&nbsp;
              Expertise
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-semibold leading-[1] tracking-tight">
              Skills &amp;
              <br />
              <span className="italic font-light text-white">
                Technologies.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-base text-[#f4f1ea]/65 leading-relaxed max-w-md md:ml-auto">
              Four years in production, I've built strong opinions about what to
              reach for first and what to keep on the bench. Here's the working
              stack.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {skills.map((group) => (
            <SkillCategoryCard key={group.category} group={group} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
